<script setup lang="ts">
import WrButton from "~/components/global/WrButton.vue";

const colorMode = useColorMode();
const isLight = computed(() => colorMode.value !== "dark");

interface CardData {
  id: string | number;
  imageDark: string;
  imageLight: string;
  imageAlt: string;
  title: string;
  description: string;
  textRight?: boolean;
  rightOffset?: string;
  descWidth?: string;
  mobileImageDark?: string;
  mobileImageLight?: string;
  svgIcon?: string;
}

const props = withDefaults(
  defineProps<{
    heading: string;
    subcopy: string;
    cards: CardData[];
    ctaText?: string;
    ctaLink?: string | null;
    sectionContainerClass?: string;
    ctaContainerClass?: string;
    ctaAppendClass?: string;
  }>(),
  {
    ctaText: "",
    ctaLink: null,
    sectionContainerClass: "mt-24 lg:mt-[100px] mb-16 lg:mb-[100px]",
    ctaContainerClass: "h-[40px] lg:h-[48px]",
    ctaAppendClass: "text-sm lg:font-normal px-3 lg:px-5",
  },
);

defineEmits<{
  "cta-clicked": [];
}>();

/* only render cards that exist (handles 3-card or 4-card data) */
const visibleCards = computed(() => props.cards.filter(Boolean));

/* Determine if card is on the left or right side of the grid */
function isLeftCard(index: number): boolean {
  return index % 2 === 0;
}

/* Determine if card is on the right side (icon first, text after) */
function isRightCard(index: number): boolean {
  return index % 2 === 1;
}
</script>

<template>
  <section :class="sectionContainerClass" :aria-label="heading">
    <!-- Heading -->
    <h2
      class="text-[28px] lg:text-[48px] font-semibold leading-tight text-center font-avant-demi"
    >
      <WrGradientText :html="heading" append-class="pb-0 lg:pb-4" />
    </h2>

    <p
      class="text-[16px] lg:text-[18px] font-normal tracking-[0.36px] mt-4 text-center px-4 font-avant"
      :class="isLight ? 'text-black' : 'text-white'"
    >
      {{ subcopy }}
    </p>

    <!-- 2x2 Cards Grid -->
    <div
      class="grid gap-14 gap-x-20 mt-10 px-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-2 max-w-[960px] mx-auto"
    >
      <template v-for="(card, idx) in visibleCards" :key="card.id">
        <div
          class="relative flex items-center"
          :class="isLeftCard(idx) ? 'justify-start' : 'justify-end'"
        >
          <!-- Glowing circle background -->
          <div
            class="absolute w-[160px] h-[160px] rounded-full"
            :class="
              isLight
                ? isLeftCard(idx)
                  ? 'bg-[linear-gradient(282.2deg,#9351CC_-3.25%,#FFFFFF_137.37%)]'
                  : 'bg-[linear-gradient(76.15deg,#9351CC_-3.25%,#FFFFFF_137.37%)]'
                : isLeftCard(idx)
                  ? 'bg-[linear-gradient(283.15deg,rgba(119,0,255,0.42)_-4.6%,rgba(119,0,255,0)_86.75%)]'
                  : 'bg-[linear-gradient(77.74deg,rgba(119,0,255,0.42)_-4.6%,rgba(119,0,255,0)_86.75%)]'
            "
            :style="isLeftCard(idx) ? { right: '-25px' } : { left: '-25px' }"
          />

          <!-- Pill card -->
          <div class="relative z-10 p-[0.45px] lg:rounded-full">
            <div
              class="relative max-h-[118px] h-full flex items-center gap-4 lg:gap-6 rounded-full px-5 lg:px-8 py-6 lg:py-7"
              :class="[
                isLight
                  ? isLeftCard(idx)
                    ? 'bg-[linear-gradient(280deg,#FFFFFF_23.69%,rgba(255,255,255,0)_79.9%)]'
                    : 'bg-[linear-gradient(90deg,#FFFFFF_23.69%,rgba(255,255,255,0)_79.9%)]'
                  : isLeftCard(idx)
                    ? 'bg-[linear-gradient(270deg,#6200B7_0%,rgba(80,0,178,0.06)_79.44%,rgba(77,0,177,0)_93.06%)]'
                    : 'bg-[linear-gradient(90deg,#6200B7_0%,rgba(80,0,178,0.06)_79.44%,rgba(77,0,177,0)_93.06%)]',
              ]"
            >
              <!-- Step number (visible on desktop) -->
              <span
                class="hidden lg:inline-flex absolute text-[72px] font-bold font-avant-demi select-none text-transparent leading-none tracking-normal"
                :class="[
                  isLeftCard(idx) ? 'left-3' : 'right-3',
                  isLight
                    ? '[-webkit-text-stroke:1.5px_#613FAA]'
                    : '[-webkit-text-stroke:1.5px_rgba(255,255,255,0.25)]',
                ]"
                :style="
                  isLeftCard(idx)
                    ? { top: '-2px', left: '2px' }
                    : { top: '-2px', right: '12px' }
                "
              >
                {{ idx + 1 }}
              </span>

              <!-- Left card: text first, icon second -->
              <template v-if="isLeftCard(idx)">
                <div class="min-w-0 flex-1 text-right z-10">
                  <h3
                    class="font-avant-demi text-[18px] lg:text-[20px] font-semibold leading-normal"
                    :class="isLight ? 'text-[#613FAA]' : 'text-white'"
                  >
                    {{ card.title }}
                  </h3>
                  <p
                    class="mt-1 font-avant text-[14px] lg:text-[15px] font-normal leading-normal tracking-[0.3px] opacity-70 z-10 overflow-y-auto no-scrollbar max-h-[3lh]"
                    :class="isLight ? 'text-[#000000]' : 'text-white'"
                  >
                    {{ card.description }}
                  </p>
                </div>
                <!-- Icon circle -->
                <div
                  class="flex-shrink-0 size-[76px] rounded-full flex items-center justify-center"
                  :class="isLight ? 'bg-[#9351CC]' : 'bg-[rgba(39,0,89,1)]'"
                >
                  <slot :name="`icon-${card.id}`" :index="idx" :card="card" />
                </div>
              </template>

              <!-- Right card: icon first, text second -->
              <template v-else>
                <!-- Icon circle -->
                <div
                  class="flex-shrink-0 size-[76px] rounded-full flex items-center justify-center text-left"
                  :class="isLight ? 'bg-[#9351CC]' : 'bg-[rgba(39,0,89,1)]'"
                >
                  <slot :name="`icon-${card.id}`" :index="idx" :card="card" />
                </div>
                <div class="min-w-0 flex-1">
                  <h3
                    class="font-avant-demi text-[18px] lg:text-[20px] font-semibold leading-normal"
                    :class="isLight ? 'text-[#613FAA]' : 'text-white'"
                  >
                    {{ card.title }}
                  </h3>
                  <p
                    class="mt-1 font-avant text-[14px] lg:text-[15px] font-normal leading-normal tracking-[0.3px] opacity-70 z-10 overflow-y-auto no-scrollbar max-h-[3lh]"
                    :class="isLight ? 'text-[#000000]' : 'text-white'"
                  >
                    {{ card.description }}
                  </p>
                </div>
              </template>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- CTA -->
    <div v-if="ctaText" class="flex justify-center mt-10">
      <WrButton
        @click="$emit('cta-clicked')"
        :container-class="ctaContainerClass"
        :append-class="ctaAppendClass"
      >
        {{ ctaText }}
      </WrButton>
    </div>
  </section>
</template>
